import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';

/**
 * Grouped info section with a small icon header and a list of key-value rows.
 * Replaces flat InfoCard grid for a more scannable, hierarchical layout.
 */
const InfoSection = ({ title, icon, accent = '#3B82F6', children, action }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2.5, sm: 3 },
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'rgba(15, 23, 42, 0.1)',
      bgcolor: '#fff',
      height: '100%',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)'
    }}
  >
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      justifyContent="space-between"
      sx={{ mb: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'rgba(15,23,42,0.08)' }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: `${accent}18`,
            color: accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& svg': { fontSize: 20, color: accent }
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: '#0f172a',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            fontSize: '0.82rem'
          }}
        >
          {title}
        </Typography>
      </Stack>
      {action}
    </Stack>
    <Stack spacing={2}>{children}</Stack>
  </Paper>
);

/**
 * Single key-value row inside an InfoSection.
 * Renders label on top (caption, uppercase) and value below (body, bold).
 */
export const InfoRow = ({ label, value, children }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        color: '#475569',
        fontWeight: 600,
        letterSpacing: 0.5,
        textTransform: 'uppercase',
        fontSize: '0.7rem',
        lineHeight: 1.3,
        display: 'block',
        mb: 0.5
      }}
    >
      {label}
    </Typography>
    {children || (
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: '#0f172a',
          lineHeight: 1.5,
          overflowWrap: 'anywhere',
          fontSize: '0.9rem'
        }}
      >
        {value || '—'}
      </Typography>
    )}
  </Box>
);

export default InfoSection;
